import { Drawer } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  getGrnDetailsForVehicleTracking,
  getStorageLocations,
  updateGrnDetailsForVehicleTracking,
} from 'src/redux/Actions/VehicleTracking';

export default function UpdateGrn({ openGrnUpdate, setOpenGrnUpdate }) {
  const dispatch = useDispatch();
  const authReducer = useSelector((state) => state.AuthReducer);
  const tenantOrganisationId = authReducer.userData.organisations?.id;
  const vehicleTrackingReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const grnForVehicleTracking = vehicleTrackingReducer?.grnForVehicleTracking;
  const storageLocationsGroupedByPlant =
    vehicleTrackingReducer?.storageLocationsGroupedByPlant;
  const [formData, setFormData] = useState({});
  function getStorageLocationsByPlantId(dataObj, plantId) {
    const plant = dataObj.find((item) => item.plant_id === plantId);
    return plant ? plant.storage_locations : [];
  }
  useEffect(() => {
    const plantIds = Array.from(
      new Set(
        (grnForVehicleTracking?.lineItems || [])
          .map((item) => item?.plant?.id)
          .filter(Boolean),
      ),
    );
    if (plantIds.length > 0) {
      dispatch(getStorageLocations(tenantOrganisationId, plantIds));
    }
  }, [grnForVehicleTracking]);

  useEffect(() => {
    if (openGrnUpdate) {
      dispatch(
        getGrnDetailsForVehicleTracking(tenantOrganisationId, openGrnUpdate),
      );
    }
  }, [dispatch, openGrnUpdate]);

  useEffect(() => {
    if (!openGrnUpdate) {
      setFormData({});
    }
  }, [openGrnUpdate]);

  const handleCheckboxClick = (itemObj) => {
    setFormData((prevData) => {
      const existing = prevData[itemObj.good_received_line_item_id] || {
        good_received_line_item_id: itemObj.good_received_line_item_id,
        purchase_order_number: itemObj.purchase_order_number,
        purchase_order_line_item_id: itemObj.purchase_order_line_item.id,
        quantity: itemObj.quantity,
        plant: itemObj.plant.id,
        storage_location_id: itemObj.storage_location?.id,
        batch_number: itemObj.batch_number,
      };
      return {
        ...prevData,
        [itemObj.good_received_line_item_id]: {
          ...existing,
          active: !prevData[itemObj.good_received_line_item_id]?.active,
        },
      };
    });
  };

  function getStorageLocationOptionsForPlant(plantId) {
    const storageLocations = getStorageLocationsByPlantId(
      storageLocationsGroupedByPlant,
      plantId,
    );
    return storageLocations.map((location) => ({
      value: location.id,
      label: `${location.sap_code} - ${location.name}`,
    }));
  }

  const handleOnChange = (e, grnLineItemId) => {
    const { name, value } = e.target;
    const item = grnForVehicleTracking?.lineItems.find(
      (i) => i.good_received_line_item_id === grnLineItemId,
    );
    if (!item) return;
    setFormData((prevData) => {
      const existing = prevData[grnLineItemId] || {
        good_received_line_item_id: item.good_received_line_item_id,
        purchase_order_number: item.purchase_order_number,
        purchase_order_line_item_id: item.purchase_order_line_item.id,
        quantity: item.quantity,
        plant: item.plant.id,
      };
      return {
        ...prevData,
        [grnLineItemId]: {
          ...existing,
          [name]: value,
          active: true,
        },
      };
    });
  };

  const handleSubmit = () => {
    const activeItems = Object.values(formData)
      .filter((dataObj) => dataObj.active)
      .map((dataObj) => ({
        good_received_line_item_id: dataObj.good_received_line_item_id,
        purchase_order_number: dataObj.purchase_order_number,
        purchase_order_line_item_id: dataObj.purchase_order_line_item_id,
        quantity: dataObj.quantity,
        plant: dataObj.plant,
        storage_location_id: dataObj.storage_location_id,
        batch_number: dataObj.batch_number,
        header_text: dataObj.header_text,
        bill_of_lading: dataObj.bill_of_lading,
        reference_document_number: dataObj.reference_document_number,
      }));
    const payload = {
      tenant_organisation_id: tenantOrganisationId,
      vehicle_tracking_number: openGrnUpdate,
      grn_unique_number: grnForVehicleTracking.grnUniqueNumber,
      items: activeItems,
    };
    dispatch(updateGrnDetailsForVehicleTracking(payload)).then(() => {
      window.location.reload();
    });
  };

  return (
    <Drawer
      anchor="right"
      open={Boolean(openGrnUpdate)}
      onClose={() => setOpenGrnUpdate(false)}
      ModalProps={{ keepMounted: false }}
      sx={{ zIndex: 1 }}
      PaperProps={{
        sx: { width: '80%' },
      }}
    >
      <div className="grn-modal-container">
        <div className="grn-modal-header">
          <span className="text-xxl text-bold">
            Update Goods Receipt: Select Line Items & Storage
          </span>
        </div>
        <div className="grn-modal-body">
          <Table hover responsive className="grn-custom-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Plant</th>
                <th>Purchase Order</th>
                <th>PO Line Item</th>
                <th>Quantity</th>
                <th>Storage Location</th>
                <th>Batch</th>
                <th>Header Text</th>
                <th>Bill of Lading</th>
                <th>Ref Doc</th>
              </tr>
            </thead>
            <tbody>
              {grnForVehicleTracking?.lineItems?.map((itemData) => {
                const grnData =
                  formData[itemData.good_received_line_item_id] || {};
                return (
                  <tr key={itemData.good_received_line_item_id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={grnData.active || false}
                        onChange={() => handleCheckboxClick(itemData)}
                      />
                    </td>
                    <td>{itemData?.plant.name}</td>
                    <td>{itemData?.purchase_order_number}</td>
                    <td>{itemData?.purchase_order_line_item.name}</td>
                    <td>
                      {itemData?.quantity} {itemData?.unit_of_measurement}
                    </td>
                    <td>
                      <Select
                        className="react-select-container"
                        isClearable
                        placeholder="Select Storage Location"
                        value={
                          getStorageLocationOptionsForPlant(
                            itemData.plant.id,
                          ).find(
                            (opt) =>
                              opt.value ===
                              (grnData.storage_location_id ??
                                itemData.storage_location?.id),
                          ) || null
                        }
                        onChange={(selectedOption) =>
                          handleOnChange(
                            {
                              target: {
                                name: 'storage_location_id',
                                value: selectedOption?.value ?? '',
                              },
                            },
                            itemData.good_received_line_item_id,
                          )
                        }
                        options={getStorageLocationOptionsForPlant(
                          itemData.plant.id,
                        )}
                      />
                    </td>
                    <td>
                      <input
                        name="batch_number"
                        className="form-input-section"
                        size="small"
                        type="text"
                        value={
                          grnData.batch_number ?? itemData?.batch_number ?? ''
                        }
                        onChange={(e) =>
                          handleOnChange(e, itemData.good_received_line_item_id)
                        }
                      />
                    </td>
                    <td>
                      <input
                        name="header_text"
                        className="form-input-section"
                        size="small"
                        type="text"
                        value={
                          grnData.header_text ?? itemData?.header_text ?? ''
                        }
                        onChange={(e) =>
                          handleOnChange(e, itemData.good_received_line_item_id)
                        }
                      />
                    </td>
                    <td>
                      <input
                        name="bill_of_lading"
                        className="form-input-section"
                        size="small"
                        type="text"
                        value={
                          grnData.bill_of_lading ??
                          itemData?.bill_of_lading ??
                          ''
                        }
                        onChange={(e) =>
                          handleOnChange(e, itemData.good_received_line_item_id)
                        }
                      />
                    </td>
                    <td>
                      <input
                        name="reference_document_number"
                        className="form-input-section"
                        size="small"
                        type="text"
                        value={
                          grnData.reference_document_number ??
                          itemData?.reference_document_number ??
                          ''
                        }
                        onChange={(e) =>
                          handleOnChange(e, itemData.good_received_line_item_id)
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
        <div className="grn-modal-footer">
          <button className="btn-sm btn-black" onClick={handleSubmit}>
            Update GRN
          </button>
        </div>
      </div>
    </Drawer>
  );
}
